<template>
    <div class="flex justify-center w-full">
        <div class="w-3/4 p-5 bg-white rounded-lg shadow-lg">
            <!-- Page de félicitations initiale -->
            <div v-if="currentStep === 0" class="flex flex-col items-center text-center p-5">
                <img src="@/assets/images/rocket.svg" alt="Rocket" class="w-60 mb-5" />
                <h1 class="text-3xl font-bold text-gray-800 mb-4">Félicitations</h1>
                <p class="text-xl text-gray-600 mb-2">
                    Vous venez de terminer avec succès les 12 modules.
                </p>
                <p class="text-lg text-gray-600 mb-4">
                    Répondez à notre petit sondage pour nous aider à améliorer l'expérience utilisateur.
                    <br />Il s'agira d'un questionnaire de <span class="font-bold">{{ questions.length }} questions</span> qui vous prendras moins de <span class="font-bold">5 minutes</span>.
                </p>
                <Button label="Commencer" class="spect-btn-primary" size="large" @click="nextStep" />
            </div>

            <!-- Questions du sondage -->
            <div v-else-if="!showThankYou && !showCertification" class="p-3">
                <h1 class="text-3xl font-bold text-gray-800 text-center mb-6">Question {{ currentStep }}</h1>
                
                <!-- Indicateur de progression avec icône de vérification -->
                <div class="flex justify-center mb-8">
                    <ul class="flex list-none p-0 m-0">
                        <li v-for="step in questions.length" :key="step" class="flex items-center">
                            <span :class="['flex items-center justify-center', 
                                          'w-8 h-8 rounded-full text-white font-medium text-sm',
                                          currentStep >= step ? 'bg-accent' : 'bg-gray-200']">
                                <i v-if="currentStep > step" class="pi pi-check"></i>
                                <template v-else>{{ step }}</template>
                            </span>
                            <div v-if="step < questions.length" :class="['h-1 w-12', currentStep > step ? 'bg-accent' : 'bg-gray-200']"></div>
                        </li>
                    </ul>
                </div>

                <!-- Question dynamique -->
                <div v-if="currentQuestion" class="mb-6">
                    <h2 class="text-xl text-center mb-8">
                        {{ currentQuestion.text }}
                    </h2>
                    <div class="flex flex-col gap-3 max-w-md mx-auto">
                        <!-- Dans la partie template, remplacer la section pour les questions avec choix par ceci: -->
                        
                        <!-- Pour les questions avec choix (radio buttons ou checkboxes) -->
                        <template v-if="currentQuestion.choices && currentQuestion.choices.length > 0">
                            <!-- Question multichoix (checkboxes) pour la question sur les formats préférés -->
                            <template v-if="currentQuestion.text && currentQuestion.text.includes('format')">
                                <div v-for="choice in currentQuestion.choices" :key="choice.id" class="field-checkbox">
                                    <Checkbox 
                                        v-model="multiChoiceAnswers[choice.id]" 
                                        :inputId="choice.id" 
                                        :binary="true" 
                                    />
                                    <label :for="choice.id" class="ml-2">{{ choice.text }}</label>
                                </div>
                            </template>
                            
                            <!-- Question d'évaluation avec étoiles -->
                            <template v-else-if="currentQuestion.text && currentQuestion.text.includes('expérience')">
                                <div v-for="choice in currentQuestion.choices" :key="choice.id" class="field-radiobutton flex items-center">
                                    <RadioButton 
                                        v-model="userAnswers[currentQuestion.id]" 
                                        :inputId="choice.id" 
                                        :name="currentQuestion.id" 
                                        :value="choice.id" 
                                    />
                                    <label :for="choice.id" class="ml-2 flex items-center">
                                        <span class="flex text-yellow-400">
                                            <template v-if="choice.id === 'd0915254-c735-447c-8546-e3cce50d8c77'">
                                                <i class="pi pi-star-fill mx-0.5" v-for="n in 5" :key="n"></i>
                                            </template>
                                            <template v-else-if="choice.id === '977744d5-4a13-41d9-8458-9d59fd0817fe'">
                                                <i class="pi pi-star-fill mx-0.5" v-for="n in 4" :key="n"></i>
                                                <i class="pi pi-star mx-0.5"></i>
                                            </template>
                                            <template v-else-if="choice.id === '19948b56-dc7e-40fe-9554-35cda27e7036'">
                                                <i class="pi pi-star-fill mx-0.5" v-for="n in 2" :key="n"></i>
                                                <i class="pi pi-star mx-0.5" v-for="n in 3" :key="n"></i>
                                            </template>
                                            <template v-else-if="choice.id === '628aed6d-a551-47f2-a925-6502a0ad730c'">
                                                <i class="pi pi-star-fill mx-0.5"></i>
                                                <i class="pi pi-star mx-0.5" v-for="n in 4" :key="n"></i>
                                            </template>
                                        </span>
                                        <span class="ml-2">{{ choice.text }}</span>
                                    </label>
                                </div>
                            </template>
                            
                            <!-- Questions à choix unique standard (radio buttons) -->
                            <template v-else>
                                <div v-for="choice in currentQuestion.choices" :key="choice.id" class="field-radiobutton">
                                    <RadioButton 
                                        v-model="userAnswers[currentQuestion.id]" 
                                        :inputId="choice.id" 
                                        :name="currentQuestion.id" 
                                        :value="choice.id" 
                                    />
                                    <label :for="choice.id" class="ml-2">{{ choice.text }}</label>
                                </div>
                            </template>
                        </template>
                        
                        <!-- Pour la question de type texte (dernière question) -->
                        <template v-else>
                            <Textarea 
                                v-model="suggestion" 
                                rows="5" 
                                class="w-full" 
                                placeholder="Entrez un message" 
                            />
                        </template>
                    </div>
                </div>

                <!-- Boutons de navigation -->
                <div class="flex justify-between mt-8">
                    <Button v-if="currentStep > 1" label="Précédent" class="spect-btn-primary" size="large" @click="prevStep" />
                    <div v-else class="w-32"></div>
                    <Button 
                        :label="currentStep === questions.length ? 'Envoyer' : 'Continuer'" 
                        class="spect-btn-primary" size="large"
                        @click="currentStep === questions.length ? submitSurvey() : nextStep()" 
                        :disabled="!canContinue"
                    />
                </div>
            </div>

            <!-- Page de certification -->
            <div v-if="showCertification" class="flex flex-col items-center text-center p-5 relative">
                <button @click="closeCertification" class="absolute top-2 right-2 text-red-500 text-2xl">
                    <i class="pi pi-times"></i>
                </button>
                <img src="@/assets/images/certification.svg" alt="Certification" class="w-96 mb-5" />
                <p class="text-xl text-gray-600 mb-6">
                    Merci d'avoir répondu à notre sondage. À présent, veuillez passer votre examen de certification.
                </p>
                <Button label="Passer l'examen" class="spect-btn-primary" size="large" @click="goToExam" />
            </div>
        </div>
    </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted } from 'vue';
import { useRouter, onBeforeRouteUpdate, onBeforeRouteEnter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { checkModulesCompletion, fetchFeedbackQuestions, submitFeedbackAnswers } from '@/services/moduleService';

// Composants PrimeVue
import Button from 'primevue/button';
import RadioButton from 'primevue/radiobutton';
import Checkbox from 'primevue/checkbox';
import Textarea from 'primevue/textarea';

const router = useRouter();
const { locale } = useI18n();

// État du formulaire
// État du formulaire
const currentStep = ref(0);
const showThankYou = ref(false);
const showCertification = ref(false);
const questions = ref([]);
const userAnswers = ref({});
const isLoading = ref(false);
const suggestion = ref(""); // Nouvelle variable pour la suggestion

// Add these navigation functions
const nextStep = () => {
  currentStep.value++;
};

const prevStep = () => {
  currentStep.value--;
};

// Récupérer les questions depuis l'API
onMounted(async () => {
  try {
    isLoading.value = true;
    const allQuestions = await fetchFeedbackQuestions();
    // Filter out the "How can you stay safe online?" question and text-type questions (without choices)
    questions.value = allQuestions.filter(q => 
      q.text !== "How can you stay safe online?"
    );
  } catch (error) {
    console.error('Erreur lors de la récupération des questions:', error);
  } finally {
    isLoading.value = false;
  }
});

// Question actuelle basée sur l'étape courante
const currentQuestion = computed(() => {
  if (currentStep.value > 0 && currentStep.value <= questions.value.length) {
    return questions.value[currentStep.value - 1];
  }
  return null;
});

// Vérifier si l'utilisateur peut continuer
// Dans la section script, après la déclaration des refs
const multiChoiceAnswers = ref({});

// Modifier la fonction canContinue pour gérer les questions à choix multiples
const canContinue = computed(() => {
  if (!currentQuestion.value) return true;
  
  const questionId = currentQuestion.value.id;
  
  // Question à choix multiples (formats préférés)
  if (currentQuestion.value.text && currentQuestion.value.text.includes('format')) {
    return Object.values(multiChoiceAnswers.value).some(value => value === true);
  }
  
  // Si c'est une question avec des choix
  if (currentQuestion.value.choices && currentQuestion.value.choices.length > 0) {
    return !!userAnswers.value[questionId];
  }
  
  // Si c'est la question de suggestion (texte), elle est optionnelle
  return true;
});

// Modifier la fonction submitSurvey pour inclure les réponses à choix multiples
const submitSurvey = async () => {
  try {
    // Formater les réponses pour l'API
    let formattedAnswers = Object.entries(userAnswers.value)
      .filter(([questionId, choiceId]) => choiceId) // Filtrer les réponses vides
      .map(([questionId, choiceId]) => ({
        question_id: questionId,
        choice_ids: [choiceId]
      }));
    
    // Ajouter les réponses à choix multiples
    if (Object.keys(multiChoiceAnswers.value).length > 0) {
      // Trouver la question sur les formats préférés par son texte
      const formatQuestion = questions.value.find(q => 
        q.text && q.text.includes('format')
      );
      
      if (formatQuestion) {
        // Ajouter chaque choix sélectionné comme une réponse séparée
        let choices = []
        Object.entries(multiChoiceAnswers.value).forEach(([choiceId, selected]) => {
          if (selected && formattedAnswers.length !== 6) {
            choices.push(choiceId);
          }
        });
        formattedAnswers.push({
          question_id: formatQuestion.id,
          choice_ids: choices
        });
      }
    }
    
    // Créer le payload avec les réponses et la suggestion
    const payload = {
      responses: formattedAnswers
    };
    
    // Ajouter la suggestion si elle existe et définir la langue
    const suggestionText = suggestion.value.trim();
    
    if (locale.value === 'en') {
      payload.suggestion_en = suggestionText;
      payload.suggestion = "";
      payload.version_lang = "en";
    } else {
      payload.suggestion = suggestionText;
      payload.suggestion_en = "";
      payload.version_lang = "fr";
    }
    
    // Envoyer les réponses à l'API
    await submitFeedbackAnswers(payload);
    
    // Afficher la page de certification
    showCertification.value = true;
  } catch (error) {
    console.error('Erreur lors de la soumission du sondage:', error);
  }
};

// Fermer la page de certification
const closeCertification = () => {
  showCertification.value = false;
  goToHome();
};

// Redirection vers l'examen
const goToExam = () => {
  router.push('/modules/exam');
};

// Redirection vers la page d'accueil
const goToHome = () => {
  router.push('/dashboard');
};
/*
onBeforeRouteEnter(async (to, from, next) => {
  const isFinished = await checkModulesCompletion(localStorage.user);
  if (!isFinished) {
    window.location.href="/modules?reason=unfinished_modules";
  }
});
*/
// Vérifier si l'utilisateur a terminé tous les modules
onBeforeRouteUpdate(async (to, from, next) => {
  const isFinished = await checkModulesCompletion(localStorage.user);
  if (!isFinished) {
    window.location.href="/modules?reason=unfinished_modules";
  }
});
</script>

<style lang="scss" scoped>
@use "../../../assets/scss/variables.scss";
@use "../../../assets/scss/app/common.scss";
.field-radiobutton, .field-checkbox {
    display: flex;
    align-items: center;
    background-color: #f8f9fa;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    transition: all 0.2s;
}

.field-radiobutton:hover, .field-checkbox:hover {
    background-color: #e9ecef;
}

/* Style pour l'indicateur de progression */
.flex.list-none li:last-child .w-12 {
    display: none;
}

.p-button-primary.bg-blue-900 {
    background-color: #1e3a8a;
    border-color: #1e3a8a;
}

.p-button-primary.bg-blue-900:hover {
    background-color: #1e40af;
    border-color: #1e40af;
}
</style>